import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { format } from 'date-fns';
import Iconify from '../../../components/iconify';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import EscalationPriorityBadge from './EscalationPriorityBadge';
import { isAdmin, isSuperAdmin } from '../../../utils/share';

const EscalationTable = ({
  escalations,
  onDeleteEscalation,
  onEditEscalation,
  currentUserId,
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy hh:mm a');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell sx={{pl:2}}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {escalations && escalations.length > 0 ? (
              escalations.map((escalation) => (
                <TableRow key={escalation.id} hover>
                  <TableCell>{escalation.project?.projectName || `Project #${escalation.projectId}`}</TableCell>
                  <TableCell>
                    {escalation.description
                      ? escalation.description.length > 100
                        ? `${escalation.description.substring(0, 100)}...`
                        : escalation.description
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <EscalationPriorityBadge severity={escalation.severity} />
                  </TableCell>
                  <TableCell>{escalation.creator?.userName || escalation.creator?.email || '-'}</TableCell>
                  <TableCell>{formatDate(escalation.createdAt)}</TableCell>
                  <TableCell align="left">
                    <Stack direction="row" spacing={1} justifyContent="left">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/escalations/${escalation.id}`);
                          }}
                        >
                          <Iconify icon="eva:eye-fill" />
                        </IconButton>
                      </Tooltip>
                      {(escalation.creator?.id === currentUserId || isAdmin() || isSuperAdmin()) && (
                        <>
                          <Tooltip title="Edit Escalation">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditEscalation(escalation.id);
                              }}
                            >
                              <Iconify icon="eva:edit-fill" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Escalation">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteEscalation(escalation.id);
                              }}
                            >
                              <Iconify icon="material-symbols:delete" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No escalations found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {count > 0 && (
        <TablePaginationCustom
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </>
  );
};

EscalationTable.propTypes = {
  escalations: PropTypes.array.isRequired,
  onDeleteEscalation: PropTypes.func.isRequired,
  onEditEscalation: PropTypes.func.isRequired,
  currentUserId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
};

export default EscalationTable;
